import { llm } from '@cotera/api';
import { Erod } from '@cotera/era';
import type { Ty } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import { jsonSchema, type ModelMessage, type ToolSet } from 'ai';
import { ToolKey } from '../tool-key';
import type { LlmCallSettings, LlmProviderOptions } from './llm-input-types';
import { mergeProviderOptions } from './model-input-base';
import type { CoteraToolDefinition } from './cotera-tool-definition';
import {
  buildZodStreamTextOutputFromShape,
  type StreamTextOutputFromShape,
} from './stream-text-output-from-shape';
import { transformCallSettings } from './transform-call-settings';

/** One tool after provider-specific schema encoding (no `execute`). */
export type AugmentedToolDefinition = {
  description: string;
  inputSchema: unknown;
  rawParams: unknown;
};

type CoteraModel = NonNullable<ReturnType<typeof llm.getModel>>;

const aiSdkProviderForModel = (model: CoteraModel): string => {
  switch (model.provider) {
    case 'Google':
      return 'google.generative-ai';
    case 'Anthropic':
      return 'anthropic.messages';
    case 'OpenAI':
    case 'AzureFoundry':
      return 'openai.responses';
    case 'LunaRoute':
      // OpenAI-compatible gateway: tools/schemas encode the same as OpenAI.
      return 'openai.chat';
    default:
      return Assert.unreachable(model.provider);
  }
};

const toolDescription = (tool: CoteraToolDefinition): string => {
  const baseDescription = tool.description ?? tool.key;
  if (tool.llmInstructions === undefined) {
    return baseDescription;
  }
  return `${baseDescription}\n\n${tool.llmInstructions}`;
};

const defaultAugmentDefinition = (
  tool: CoteraToolDefinition,
  aiSdkProvider: string
): AugmentedToolDefinition => {
  const rawParams = tool.inputSchema.zodSchema({ strict: true });
  return {
    description: toolDescription(tool),
    inputSchema: jsonSchema(
      Erod.erodToJsonSchema(tool.inputSchema, {
        provider: Erod.resolveLlmProvider(aiSdkProvider),
      })
    ),
    rawParams,
  };
};

const googleAugmentDefinition = (
  tool: CoteraToolDefinition
): AugmentedToolDefinition => {
  const rawParams = tool.inputSchema.zodSchema({ strict: true });
  return {
    description: toolDescription(tool),
    inputSchema: rawParams,
    rawParams,
  };
};

const mapMessagePartProviderOptions = (
  message: ModelMessage,
  normalize: (
    providerOptions: Record<string, unknown>
  ) => Record<string, unknown>
): ModelMessage => {
  if (message.content === undefined || !Array.isArray(message.content)) {
    return message;
  }
  return {
    ...message,
    content: message.content.map((contentItem: unknown) => {
      const item = contentItem as {
        providerOptions?: unknown;
        [key: string]: unknown;
      };
      if (
        item.providerOptions !== undefined &&
        typeof item.providerOptions === 'object' &&
        item.providerOptions !== null &&
        !Array.isArray(item.providerOptions)
      ) {
        return {
          ...item,
          providerOptions: normalize(
            item.providerOptions as Record<string, unknown>
          ),
        };
      }
      return contentItem;
    }),
  } as ModelMessage;
};

const finalizeMessagesWithPartNormalizer = (
  messages: ModelMessage[],
  normalize: (
    providerOptions: Record<string, unknown>
  ) => Record<string, unknown>
): ModelMessage[] => {
  return messages.map((message) =>
    mapMessagePartProviderOptions(message, normalize)
  );
};

/**
 * GLM (Zhipu) served through the LunaRoute gateway rejects a `user` message
 * that directly follows a `tool` message — `invalid_request_message_order`
 * (code 3230), surfaced to us as `Stream produced no output`. It requires an
 * assistant turn to consume a tool result before any further user input.
 *
 * The conversation renderer legitimately appends user-role content after the
 * newest tool result — the ingot schema (always last), the per-turn
 * facts/skills block, the tail timestamp, and tool image outputs — which
 * produces exactly that `tool -> user` adjacency. OpenAI/Anthropic/Azure
 * tolerate it; GLM does not. Bridge each such boundary with an empty assistant
 * message so the role sequence is valid. The content is `''` so we satisfy the
 * ordering rule (which is all GLM checks here) without putting words in the
 * model's mouth; the added context cost is a single empty-content turn
 * (negligible tokens). This is deliberately scoped to the LunaRoute adapter —
 * other providers accept the `tool -> user` sequence unchanged.
 */
const bridgeToolToUserWithAssistant = (
  messages: ModelMessage[]
): ModelMessage[] => {
  const out: ModelMessage[] = [];
  for (const message of messages) {
    const previous = out[out.length - 1];
    if (
      previous !== undefined &&
      previous.role === 'tool' &&
      message.role === 'user'
    ) {
      out.push({ role: 'assistant', content: '' });
    }
    out.push(message);
  }
  return out;
};

/**
 * Provider-specific augmentations applied at {@link LLMInputBuilder.build} time
 * and when rendering tools for {@link streamText}.
 */
export type ModelInputAdapter<TModelId extends string = string> = {
  readonly modelId: TModelId;
  /** Provider-specific cleanup on message parts before {@link streamText}. */
  finalizeMessages(messages: ModelMessage[]): ModelMessage[];
  buildStreamTextOutputFromShape(
    outputShape: Ty.ExtendedAttributeType
  ): StreamTextOutputFromShape;
  getCallSettings(
    baseCallSettings: LlmCallSettings | undefined
  ): LlmCallSettings;
  mergeProviderOptions(
    draftProviderOptions: LlmProviderOptions | undefined,
    callSettings: LlmCallSettings
  ): LlmProviderOptions | undefined;
  augmentDefinition(tool: CoteraToolDefinition): AugmentedToolDefinition;
  renderTools(toolDefinitions: readonly CoteraToolDefinition[]): ToolSet;
};

/** Shared adapter behavior; provider subclasses override {@link finalizeMessages}. */
export class ModelInputAdapterBase<TModelId extends string = string>
  implements ModelInputAdapter<TModelId>
{
  readonly modelId: TModelId;
  protected readonly coteraModel: CoteraModel;
  private readonly aiSdkProvider: string;

  constructor(modelId: TModelId) {
    const coteraModel = llm.getModel(modelId);
    if (coteraModel === undefined) {
      throw new Error(`Model ${modelId} does not exist`);
    }
    this.modelId = modelId;
    this.coteraModel = coteraModel;
    this.aiSdkProvider = aiSdkProviderForModel(coteraModel);
  }

  finalizeMessages(messages: ModelMessage[]): ModelMessage[] {
    return messages;
  }

  buildStreamTextOutputFromShape(
    outputShape: Ty.ExtendedAttributeType
  ): StreamTextOutputFromShape {
    return buildZodStreamTextOutputFromShape(outputShape);
  }

  getCallSettings(
    baseCallSettings: LlmCallSettings | undefined
  ): LlmCallSettings {
    const { providerOptions: _po, ...base } = baseCallSettings ?? {};
    return transformCallSettings(this.modelId, base);
  }

  mergeProviderOptions(
    draftProviderOptions: LlmProviderOptions | undefined,
    callSettings: LlmCallSettings
  ): LlmProviderOptions | undefined {
    return mergeProviderOptions(
      draftProviderOptions,
      callSettings.providerOptions
    );
  }

  augmentDefinition(tool: CoteraToolDefinition): AugmentedToolDefinition {
    return defaultAugmentDefinition(tool, this.aiSdkProvider);
  }

  renderTools(toolDefinitions: readonly CoteraToolDefinition[]): ToolSet {
    return Object.fromEntries(
      toolDefinitions.map((tool) => {
        const augmented = this.augmentDefinition(tool);
        return [
          ToolKey.fromString(tool.key).toLlmName(),
          {
            rawParams: augmented.rawParams,
            description: augmented.description,
            inputSchema: augmented.inputSchema,
          },
        ];
      })
    ) as ToolSet;
  }
}

/**
 * Provider-option keys the AI SDK's Responses API client stores replay
 * `itemId`s under: `openai` for OpenAI, `azure` for Azure Foundry deployments
 * served over Responses (`Model.azureApi === 'responses'`, e.g. GPT-6 Astra).
 */
const isResponsesItemIdProvider = (provider: string): boolean =>
  provider === 'openai' ||
  provider.startsWith('openai.') ||
  provider === 'azure' ||
  provider.startsWith('azure.');

/**
 * OpenAI / Azure Foundry: strip replay `itemId` noise from message parts.
 *
 * With an `itemId` present (and the default `store: true`), the AI SDK replays
 * an assistant text/tool-call part as an `item_reference` to the stored
 * Responses item. The renderer prunes reasoning parts from history, so the
 * `rs_…` item a `msg_…`/`fc_…` item depends on is never sent, and the API 400s
 * ("was provided without its required 'reasoning' item"). Without the id the
 * part is sent inline instead, which needs no reasoning item.
 */
abstract class OpenAiCompatibleModelInputAdapter<
  TModelId extends string = string
> extends ModelInputAdapterBase<TModelId> {
  private static stripOpenAiItemId(
    providerOptions: Record<string, unknown>
  ): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(providerOptions).map(([provider, options]) => {
        if (
          options !== null &&
          typeof options === 'object' &&
          !Array.isArray(options) &&
          isResponsesItemIdProvider(provider)
        ) {
          const { itemId: _itemId, ...restOptions } = options as Record<
            string,
            unknown
          >;
          return [provider, restOptions];
        }
        return [provider, options];
      })
    );
  }

  override finalizeMessages(messages: ModelMessage[]): ModelMessage[] {
    return finalizeMessagesWithPartNormalizer(
      messages,
      OpenAiCompatibleModelInputAdapter.stripOpenAiItemId
    );
  }
}

/** Anthropic: Zod structured output, ephemeral cache, OpenAI itemId strip on replay. */
export class AnthropicModelInputAdapter<
  TModelId extends string = string
> extends OpenAiCompatibleModelInputAdapter<TModelId> {
  override buildStreamTextOutputFromShape(
    outputShape: Ty.ExtendedAttributeType
  ): StreamTextOutputFromShape {
    return buildZodStreamTextOutputFromShape(outputShape);
  }

  override mergeProviderOptions(
    draftProviderOptions: LlmProviderOptions | undefined,
    callSettings: LlmCallSettings
  ): LlmProviderOptions | undefined {
    return mergeProviderOptions(
      mergeProviderOptions(draftProviderOptions, callSettings.providerOptions),
      {
        anthropic: {
          cacheControl: { type: 'ephemeral' },
        },
      }
    );
  }
}

/** Google Gemini: thought signatures, Zod tools, Zod structured output. */
export class GoogleModelInputAdapter<
  TModelId extends string = string
> extends ModelInputAdapterBase<TModelId> {
  private static readThoughtSignature(opts: unknown): string | undefined {
    if (
      opts === null ||
      opts === undefined ||
      typeof opts !== 'object' ||
      Array.isArray(opts)
    ) {
      return undefined;
    }
    const o = opts as Record<string, unknown>;
    const ts = o['thoughtSignature'] ?? o['thought_signature'];
    return typeof ts === 'string' && ts.length > 0 ? ts : undefined;
  }

  private static stripOpenAiItemId(
    providerOptions: Record<string, unknown>
  ): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(providerOptions).map(([provider, options]) => {
        if (
          options !== null &&
          typeof options === 'object' &&
          !Array.isArray(options) &&
          (provider === 'openai' || provider.startsWith('openai.'))
        ) {
          const { itemId: _itemId, ...restOptions } = options as Record<
            string,
            unknown
          >;
          return [provider, restOptions];
        }
        return [provider, options];
      })
    );
  }

  private static normalizePartProviderOptions(
    providerOptions: Record<string, unknown>
  ): Record<string, unknown> {
    const stripped = GoogleModelInputAdapter.stripOpenAiItemId(providerOptions);

    const findGeminiRelatedThoughtSignature = (): string | undefined => {
      for (const [key, opts] of Object.entries(stripped)) {
        if (
          key === 'google' ||
          key === 'vertex' ||
          key === 'google.generative-ai' ||
          key.startsWith('google.') ||
          key.includes('vertex')
        ) {
          const ts = GoogleModelInputAdapter.readThoughtSignature(opts);
          if (ts !== undefined) {
            return ts;
          }
        }
      }
      return undefined;
    };

    const sigGoogle = GoogleModelInputAdapter.readThoughtSignature(
      stripped['google']
    );
    const sigVertex = GoogleModelInputAdapter.readThoughtSignature(
      stripped['vertex']
    );
    if (sigGoogle !== undefined && sigVertex !== undefined) {
      return stripped;
    }

    const fromAny = findGeminiRelatedThoughtSignature();
    if (fromAny === undefined) {
      return stripped;
    }

    let out = stripped;
    if (sigGoogle === undefined) {
      const cur = out['google'];
      const base =
        cur !== null && typeof cur === 'object' && !Array.isArray(cur)
          ? { ...(cur as Record<string, unknown>) }
          : {};
      out = { ...out, ['google']: { ...base, thoughtSignature: fromAny } };
    }
    if (sigVertex === undefined) {
      const cur = out['vertex'];
      const base =
        cur !== null && typeof cur === 'object' && !Array.isArray(cur)
          ? { ...(cur as Record<string, unknown>) }
          : {};
      out = { ...out, ['vertex']: { ...base, thoughtSignature: fromAny } };
    }
    return out;
  }

  override finalizeMessages(messages: ModelMessage[]): ModelMessage[] {
    return finalizeMessagesWithPartNormalizer(
      messages,
      GoogleModelInputAdapter.normalizePartProviderOptions
    );
  }

  override buildStreamTextOutputFromShape(
    outputShape: Ty.ExtendedAttributeType
  ): StreamTextOutputFromShape {
    return buildZodStreamTextOutputFromShape(outputShape);
  }

  override augmentDefinition(
    tool: CoteraToolDefinition
  ): AugmentedToolDefinition {
    return googleAugmentDefinition(tool);
  }
}

/**
 * OpenAI: Zod {@link Output.object} — AI SDK converts Zod to strict JSON Schema
 * when `strictJsonSchema` is set in provider options.
 */
export class OpenAIModelInputAdapter<
  TModelId extends string = string
> extends OpenAiCompatibleModelInputAdapter<TModelId> {}

/** Azure Foundry: same message finalization as {@link OpenAIModelInputAdapter}. */
export class AzureFoundryModelInputAdapter<
  TModelId extends string = string
> extends OpenAiCompatibleModelInputAdapter<TModelId> {}

/**
 * LunaRoute gateway (GLM/Zhipu): shares the OpenAI part-level finalization, then
 * repairs the one message-ordering rule GLM enforces more strictly than OpenAI —
 * no `user` message directly after a `tool` message.
 * @see {@link bridgeToolToUserWithAssistant}
 */
export class LunaRouteModelInputAdapter<
  TModelId extends string = string
> extends OpenAiCompatibleModelInputAdapter<TModelId> {
  override finalizeMessages(messages: ModelMessage[]): ModelMessage[] {
    return bridgeToolToUserWithAssistant(super.finalizeMessages(messages));
  }
}

/** No provider-specific augmentations beyond shared call-setting transforms. */
export const defaultModelAdapter = <TModelId extends string>(
  modelId: TModelId
): ModelInputAdapter<TModelId> => new ModelInputAdapterBase(modelId);

/** @see {@link AnthropicModelInputAdapter} */
export const anthropicModelAdapter = <TModelId extends string>(
  modelId: TModelId
): ModelInputAdapter<TModelId> => new AnthropicModelInputAdapter(modelId);

/** @see {@link GoogleModelInputAdapter} */
export const googleModelAdapter = <TModelId extends string>(
  modelId: TModelId
): ModelInputAdapter<TModelId> => new GoogleModelInputAdapter(modelId);

/** @see {@link OpenAIModelInputAdapter} */
export const openaiModelAdapter = <TModelId extends string>(
  modelId: TModelId
): ModelInputAdapter<TModelId> => new OpenAIModelInputAdapter(modelId);

/** @see {@link AzureFoundryModelInputAdapter} */
export const azureFoundryModelAdapter = <TModelId extends string>(
  modelId: TModelId
): ModelInputAdapter<TModelId> => new AzureFoundryModelInputAdapter(modelId);

export const modelAdapterFor = <TModelId extends string>(
  modelId: TModelId
): ModelInputAdapter<TModelId> => {
  const provider = llm.getModel(modelId)?.provider;
  if (provider === 'Anthropic') {
    return new AnthropicModelInputAdapter(modelId);
  }
  if (provider === 'Google') {
    return new GoogleModelInputAdapter(modelId);
  }
  if (provider === 'OpenAI') {
    return new OpenAIModelInputAdapter(modelId);
  }
  if (provider === 'AzureFoundry') {
    return new AzureFoundryModelInputAdapter(modelId);
  }
  if (provider === 'LunaRoute') {
    return new LunaRouteModelInputAdapter(modelId);
  }
  return new ModelInputAdapterBase(modelId);
};
